import type { Edit } from '@w/crdt'
import type { Block } from '@w/content-blocks'
import { isObject } from '@w/utils'
import { stripRuntimeState } from '@w/content-blocks-editor/runtime-state'
import { pageDocumentInput } from '../db/page-document'
import { editEntry } from './entry-edit'
import { mintQuestionBlocks } from './entry-blocks'
import { mintedCards } from './lexicon-cards'
import { type LexiconEntry, type TranslationLanguage } from './lexicon-model'

// INV-1: entry questions reflect the merged entry; unrelated remote questions keep their identities and content.
export function editEntryQuestions(tx: Edit, currentEntry: LexiconEntry, next: LexiconEntry, translationLanguage: TranslationLanguage, removedQuestionIds?: ReadonlySet<string>): void {
  editEntry(tx, currentEntry, next)
  const content = tx.read('content')
  if (!isObject(content) || !Array.isArray(content.children)) throw new Error('Missing entry question list')
  // decisions.md:194 — ordinary edits add newly minted ids only; merged
  // structural fields retain remote senses omitted from a stale caller's draft.
  const entry = { ...next, ...(isObject(content.entry)?content.entry:{}) }
  const previousMintedIds=new Set(mintedCards(currentEntry).map(card=>card.qid))
  const existing = content.children as unknown as Block[]
  const oldQuestionIds = new Set(_questionBlocks(existing).map(block => block.qid))
  const minted = mintedCards(entry)
  const desiredIds = new Set(minted.map(card => card.qid))
  let appendAt = existing.length
  for (let index = existing.length - 1; index >= 0; index--) {
    const block = existing[index]
    if (_questionBlocks([block]).some(part => !desiredIds.has(part.qid))) {
      tx.remove('content.children', index, 1)
      appendAt--
    }
  }
  const added = minted.filter(card => !oldQuestionIds.has(card.qid) && !previousMintedIds.has(card.qid) && !removedQuestionIds?.has(card.qid)).flatMap(card => mintQuestionBlocks(entry, card.qid, card.kind, translationLanguage))

  if (added.length > 0) tx.insert('content.children', appendAt,
    pageDocumentInput(stripRuntimeState(added)) as Parameters<typeof tx.insert>[2])
}

function _questionBlocks(
  blocks: Block[],
): Array<Extract<Block, { type: 'question' | 'cloze' }>> {
  const result: Array<Extract<Block, { type: 'question' | 'cloze' }>> = []
  const visit = (block: Block) => {
    if (block.type === 'question' || block.type === 'cloze') result.push(block)
    if ('children' in block && Array.isArray(block.children)) {
      for (const child of block.children) {
        if (typeof child === 'object' && child !== null && 'type' in child)
          visit(child)
      }
    }
  }
  for (const block of blocks) visit(block)
  return result
}
